import React, { useState } from 'react';
import {
  Modal,
  FormField,
  Textarea,
  Button,
  SpaceBetween,
  Icon,
  Box,
} from '@cloudscape-design/components';
import { ASSIGNEE_LABELS, ERROR_MESSAGES } from 'src/contstant/commonLabels';
import RequiredFieldLabel from '../RequiredFieldLabel';
import EmployeePicker from '../common/EmployeePicker';
import useUserProfile from 'src/hooks/useUserProfile';

interface ChangeAssigneeModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: (data: { newAssignee: string; comment: string }) => void;
  currentAssignee?: string;
}

export const ChangeAssigneeModal: React.FC<ChangeAssigneeModalProps> = ({
  visible,
  onDismiss,
  onConfirm,
  currentAssignee,
}) => {
  const [newAssignee, setNewAssignee] = useState('');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<{ assignee?: string; comment?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const { getUserProgramId } = useUserProfile();

  const validateForm = () => {
    const newErrors: { assignee?: string; comment?: string } = {};

    // EmployeePicker only commits logins that match a PAPI suggestion, so
    // we just verify a login was selected. Format is guaranteed lowercase.
    if (!newAssignee) {
      newErrors.assignee = ERROR_MESSAGES.ASSIGNEE_REQUIRED;
    }

    if (!comment.trim()) {
      newErrors.comment = 'Comment is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (validateForm()) {
      setIsLoading(true);
      try {
        await onConfirm({
          newAssignee,
          comment: comment.trim(),
        });
        setNewAssignee('');
        setComment('');
        setErrors({});
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDismiss = () => {
    setNewAssignee('');
    setComment('');
    setErrors({});
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
          <Icon name="user-profile" variant="normal" />
          <span>Change Task Assignee</span>
        </SpaceBetween>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="normal"
              onClick={handleDismiss}
              disabled={isLoading}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={isLoading}
              disabled={isLoading}
              data-testid="confirm-button"
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <FormField
          label={<RequiredFieldLabel label={ASSIGNEE_LABELS.LABEL} />}
          errorText={errors.assignee}
          data-testid="enter-assignee-name-field"
        >
          <EmployeePicker
            programId={getUserProgramId()}
            value={newAssignee}
            data-testid="enter-assignee-name-input"
            onSelect={(login) => {
              setNewAssignee(login);
              if (errors.assignee) {
                setErrors((prev) => ({ ...prev, assignee: undefined }));
              }
            }}
            onClear={() => setNewAssignee('')}
            placeholder={ASSIGNEE_LABELS.PLACEHOLDER}
            invalid={!!errors.assignee}
          />
        </FormField>

        <FormField
          label={<RequiredFieldLabel label="Comment" />}
          errorText={errors.comment}
        >
          <Textarea
            value={comment}
            onChange={({ detail }) => setComment(detail.value)}
            placeholder="Explain why you're changing the assignee"
            rows={3}
            data-testid="comment-field"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
